/**
 * useStagedResourceResolve (GDN-2324)
 *
 * Per-row "decide = commit now" for the admin staged-resources browser, the
 * admin counterpart to the practice inbox's `useOrgReviewInbox`. Instead of
 * staging a decision into the batch-commit maps, a match/resolve pick fires the
 * dedicated single-row endpoint and commits immediately:
 *
 *   - `matchRow`   → POST .../resources/{id}/match    (ambiguous → link / create-new)
 *   - `resolveRow` → POST .../resources/{id}/resolve  (conflict → keep/take/skip/…)
 *
 * A clean pick commits and the host refetches so the row drops; a match that
 * comes back `needs_resolution` becomes a concrete conflict for the field picker
 * (host clears the now-moot match pick). The existing whole-batch Commit is
 * untouched — resolved rows leave the staged queue, so the batch gate clears on
 * its own and any stale local-map entry for a committed row is an idempotent
 * no-op server-side (same "harmless leftover" contract the scoped commit relies
 * on).
 */

import { useCallback, useRef, useState } from 'react';
import type { ReviewMatchResolution } from '@features/admin-v2/components/import-review-types';
import type { ResolveStagedResourceRequest } from '@store/generated/data-migration';
import { useSnackbar } from 'notistack';

import {
  useMatchStagedResourceMutation,
  useResolveStagedResourceMutation,
} from '../store/hooks';
import { readLaunchRefusal } from '../utils/launch-refusal';

/**
 * What to tell the operator when a resolve or match fails. A 4xx is the
 * server refusing, with its own reason — for example a row someone held
 * internally after this page loaded (GDN-4065) — and retrying cannot change
 * that answer, so show the reason instead of "try again".
 */
function failureMessage(error: unknown, fallback: string): string {
  const failure = readLaunchRefusal(error);
  const refused =
    failure.failureClass === 'http' &&
    failure.status !== undefined &&
    failure.status >= 400 &&
    failure.status < 500;
  return refused && failure.message ? failure.message : fallback;
}

/** The resolve endpoint's action vocabulary (conflict → commit). */
export type ResolveAction = ResolveStagedResourceRequest['action'];

/**
 * Per-field current/imported choices for a survivorship resolve (the
 * field_overrides carried on `resolveRow`).
 */
export type FieldPicks = Record<string, 'existing' | 'incoming'>;

export interface UseStagedResourceResolveArgs {
  organizationId: string;
  sessionId: string;
  /**
   * Refetch the review data. Called after EVERY server-state-changing outcome —
   * a commit (the row leaves the staged queue) AND a match's `needs_resolution`
   * (the row is reclassified ambiguous→conflict and gains competing fields).
   * The generated match/resolve mutations carry no cache tags, so nothing
   * auto-invalidates `list`/`browse`/`conflicts`; without this the row's verdict
   * and competing fields stay stale and it can't flip to the conflict UI. NOT
   * called on error — nothing changed server-side.
   */
  refetch: () => void;
  /**
   * A row committed (a clean match/create, or a resolved conflict). The host
   * should clear the row's optimistic pick(s); the refetch drops the row.
   */
  onCommitted: (importResourceId: string) => void;
  /**
   * A match returned `needs_resolution` — the chosen record has competing
   * fields, so the row is now a concrete conflict. The host should clear the
   * (now-moot) match pick; the refetch pulls the new verdict + competing fields
   * so the whole-record decision buttons render.
   */
  onNeedsResolution: (importResourceId: string) => void;
  /**
   * The mutation failed. The host should clear the local pick it set optimistically
   * so the row's buttons return to their pre-pick state for a retry.
   */
  onError: (importResourceId: string) => void;
}

export interface UseStagedResourceResolveReturn {
  /**
   * import_resource_ids whose match/resolve is in flight (or has committed and
   * is awaiting the refetch that drops the row). Feed to
   * `ImportReviewBrowser.disabledRowIds` so the row's controls disable.
   */
  resolvingIds: ReadonlySet<string>;
  /** Resolve + commit a conflict/decision row now (survivorship carries overrides). */
  resolveRow: (
    importResourceId: string,
    action: ResolveAction,
    fieldOverrides?: FieldPicks,
  ) => void;
  /** Link an ambiguous row to a candidate (or create-new) and commit now. */
  matchRow: (importResourceId: string, resolution: ReviewMatchResolution) => void;
}

export function useStagedResourceResolve({
  organizationId,
  sessionId,
  refetch,
  onCommitted,
  onNeedsResolution,
  onError,
}: UseStagedResourceResolveArgs): UseStagedResourceResolveReturn {
  const { enqueueSnackbar } = useSnackbar();
  const [resolve] = useResolveStagedResourceMutation();
  const [match] = useMatchStagedResourceMutation();

  // Synchronous duplicate-fire guard: a click handler can't read fresh state in
  // the same tick, so the ref is the source of truth and `resolvingIds` mirrors
  // it into state so the row can visually disable. A row stays locked after a
  // successful commit (the refetch drops it) and only unlocks on error/needs-
  // resolution, to allow a retry or hand off to the field picker.
  const inFlightRef = useRef<Set<string>>(new Set());
  const [resolvingIds, setResolvingIds] = useState<ReadonlySet<string>>(new Set());

  const lockRow = useCallback((id: string) => {
    inFlightRef.current.add(id);
    setResolvingIds(new Set(inFlightRef.current));
  }, []);

  const unlockRow = useCallback((id: string) => {
    inFlightRef.current.delete(id);
    setResolvingIds(new Set(inFlightRef.current));
  }, []);

  const resolveRow = useCallback(
    (importResourceId: string, action: ResolveAction, fieldOverrides?: FieldPicks) => {
      if (inFlightRef.current.has(importResourceId)) return;
      lockRow(importResourceId);
      void (async () => {
        try {
          await resolve({
            sessionId,
            importResourceId,
            resolveStagedResourceRequest: {
              organization_id: organizationId,
              action,
              ...(fieldOverrides ? { field_overrides: fieldOverrides } : {}),
            },
          }).unwrap();
          // Committed: leave the row locked; the refetch drops it from the list.
          enqueueSnackbar('Row committed.', { variant: 'success' });
          onCommitted(importResourceId);
          refetch();
        } catch (error) {
          enqueueSnackbar(
            failureMessage(error, 'Could not commit this row. Please try again.'),
            { variant: 'error' },
          );
          onError(importResourceId);
          unlockRow(importResourceId);
        }
      })();
    },
    [
      resolve,
      sessionId,
      organizationId,
      enqueueSnackbar,
      refetch,
      onCommitted,
      onError,
      lockRow,
      unlockRow,
    ],
  );

  const matchRow = useCallback(
    (importResourceId: string, resolution: ReviewMatchResolution) => {
      if (inFlightRef.current.has(importResourceId)) return;
      lockRow(importResourceId);
      const targetPatientId =
        resolution.action === 'link' ? resolution.targetPatientId : null;
      void (async () => {
        try {
          const result = await match({
            sessionId,
            importResourceId,
            matchStagedResourceRequest: {
              organization_id: organizationId,
              target_patient_id: targetPatientId,
            },
          }).unwrap();
          if (result.outcome === 'needs_resolution') {
            enqueueSnackbar('Matched — now choose which values to keep.', {
              variant: 'info',
            });
            onNeedsResolution(importResourceId);
            unlockRow(importResourceId);
            // The row was reclassified ambiguous→conflict server-side; refetch so
            // its new verdict + competing fields land and the conflict UI renders.
            refetch();
            return;
          }
          // committed: leave the row locked; the refetch drops it from the list.
          enqueueSnackbar(
            resolution.action === 'create'
              ? 'Created a new record.'
              : 'Linked to the existing record.',
            { variant: 'success' },
          );
          onCommitted(importResourceId);
          refetch();
        } catch (error) {
          enqueueSnackbar(
            failureMessage(error, 'Could not match this record. Please try again.'),
            { variant: 'error' },
          );
          onError(importResourceId);
          unlockRow(importResourceId);
        }
      })();
    },
    [
      match,
      sessionId,
      organizationId,
      enqueueSnackbar,
      refetch,
      onCommitted,
      onNeedsResolution,
      onError,
      lockRow,
      unlockRow,
    ],
  );

  return { resolvingIds, resolveRow, matchRow };
}
